import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useV6GridEvents } from './hooks/useV6GridEvents';
import { useV6GridInteractions } from './hooks/useV6GridInteractions';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import EventCard from '../events/EventCard';
import useScheduleDayEventsLoad from '../hooks/useScheduleDayEventsLoad';

/**
 * Grid view using adapted grid positions from schedule state.
 *
 * For each cell we fabricate a lightweight event object that can be
 * rendered by ScheduleEventV6, reusing the legacy event UI.
 */
const GridView = ({ selectedDate, noClSheets, hasEditAccess }) => {
  useScheduleDayEventsLoad(selectedDate);

  const { gridCells, rowsCount, colsCount } = useV6GridEvents(selectedDate);
  const { modalInfo, closeModal, swapEvents, getCellHandlers } =
    useV6GridInteractions({ gridCells, hasEditAccess });

  const rows = useMemo(() => new Array(rowsCount).fill(null), [rowsCount]);
  const cols = useMemo(() => new Array(colsCount).fill(null), [colsCount]);

  return (
    <div className="schedule-v6-grid-view">
      <table>
        <tbody>
          {rows.map((_, rowIndex) => (
            <tr key={rowIndex}>
              {cols.map((_, colIndex) => {
                const position = rowIndex * colsCount + colIndex;
                const entry = gridCells[position];
                const handlers = getCellHandlers(position);

                if (!entry) {
                  return <td key={colIndex} {...handlers} />;
                }

                const { sheet, project } = entry;
                const event = {
                  id: sheet._id,
                  title: project?.route || project?.jobNumber || 'Worklog',
                  start: entry.start,
                  end: entry.end,
                  extendedProps: entry,
                };

                return (
                  <td key={colIndex} {...handlers}>
                    <div
                      draggable={hasEditAccess}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('sheetsId', sheet._id);
                      }}
                      className="schedule-v6-grid-view-cell"
                    >
                      <EventCard event={event} eventType='grid' noClSheets={noClSheets} hasEditAccess={hasEditAccess} />
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {!!modalInfo && (
        <ConfirmDialog
          isOpen
          onClose={closeModal}
          onSubmit={swapEvents}
          text="Two worklogs cannot occupy the same cell"
          cancelBtn="Keep original"
          confirmBtn="Swap cells"
          loadingOnSubmit
        />
      )}
    </div>
  );
};

GridView.propTypes = {
  calendarRef: PropTypes.object,
  selectedDate: PropTypes.instanceOf(Date),
  dateFormat: PropTypes.string,
  timeFormat: PropTypes.string,
  noClSheets: PropTypes.arrayOf(PropTypes.string),
  hasEditAccess: PropTypes.bool,
};

export default GridView;

